import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { create } from "zustand";
import { usePlanFeatureAccess } from "@/core/hooks/usePlanAccess";
import { useResetOnSectorChange } from "@/shared/hooks/useResetOnSectorChange";
import { isPageAccessible, planFeatureForPath } from "./gateReturn";
import { useSectorPermissions } from "./useSectorPermissions";

type LastAccessiblePageState = {
  /** Último pathname do app cujo gate estava liberado (null: nenhum ainda). */
  path: string | null;
  setPath: (path: string) => void;
  clear: () => void;
};

/**
 * Origem do "Voltar para ..." do bloqueio de recurso. Regra: a cada troca de
 * rota do MainLayout, o pathname entra aqui só se a página abre com as
 * permissões e o plano do setor ativo; página bloqueada (por permissão ou por
 * plano) não sobrescreve. Quem cai num
 * bloqueio volta para a última página liberada (PermissionGate lê isto e
 * confere de novo com as permissões do momento, em `resolveGateReturn`).
 */
export const useLastAccessiblePageStore = create<LastAccessiblePageState>(
  (set) => ({
    path: null,
    setPath: (path) => set((state) => (state.path === path ? state : { path })),
    clear: () => set({ path: null }),
  })
);

// Referência estável para o useResetOnSectorChange (o efeito depende dela).
const clearLastAccessiblePage = () =>
  useLastAccessiblePageStore.getState().clear();

/**
 * Rastreia a última página liberada. Chamado uma vez, no MainLayout. A origem
 * é esquecida ao trocar de setor (o AccountSwitcher não remonta o layout, e a
 * origem pode ser uma conversa ou automação do setor anterior: SA-1277) e ao
 * sair do layout (logout, onboarding).
 */
export function useTrackLastAccessiblePage(): void {
  const { pathname } = useLocation();
  const { permissions, isLoading } = useSectorPermissions();
  // Plano pendente conta como travado: a página só vira origem quando se sabe
  // que ela abre (o efeito roda de novo quando o plano chega). Só o recurso
  // cuja trava vale neste pathname: o catálogo de Integrações abre fora do
  // plano e não espera por ele (SA-1360 P4-C).
  const planAccess = usePlanFeatureAccess(planFeatureForPath(pathname));
  const isPlanLocked = planAccess.status !== "allowed";

  useEffect(() => {
    // Sem as permissões ainda não dá para saber se a página abre.
    if (isLoading) return;
    if (isPageAccessible(pathname, permissions, isPlanLocked)) {
      useLastAccessiblePageStore.getState().setPath(pathname);
    }
  }, [pathname, permissions, isLoading, isPlanLocked]);

  // Depois do rastreio de propósito: se os dois rodarem no mesmo commit, vale
  // a limpeza (o pathname acabou de ser julgado com as permissões do setor
  // anterior, que o keepPreviousData mantém até as novas chegarem).
  useResetOnSectorChange(clearLastAccessiblePage);

  // Limpa ao desmontar (a função devolvida é o cleanup do efeito).
  useEffect(() => clearLastAccessiblePage, []);
}
